import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { settingsService, clientsService, sitesService, actionPlansService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import RegionSettings from '../components/RegionSettings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Settings, Loader2, Save, Calendar, Users, Palette, MapPin, PoundSterling, 
  FileText, Mail, Edit3, Eye, ChevronDown, AlertCircle, TrendingUp, Plus, Trash2, X, Building2, MapPinned, Shield, ListChecks
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { EmailPreview } from '@/components/ui/email-preview';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import MetricsTemplatesSettings from '../components/MetricsTemplatesSettings';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getApiError } from '@/lib/utils';

// Default threshold values - New structure with universal and custom thresholds
const DEFAULT_THRESHOLDS = {
  // Universal Stock Result thresholds
  stock_result_green_upper: 0.5,
  stock_result_green_lower: -2.0,
  stock_result_red_upper: null,  // Above green_upper
  stock_result_red_lower: null,  // Below green_lower
  // Custom thresholds for specific customers/sites
  custom_thresholds: [],
  // COS thresholds (unchanged)
  cos_variance_green: 0.5,
  cos_variance_amber: 0.5,
  // Legacy fields (for backward compatibility)
  stock_result_red_below: null,
  stock_result_green_above: null
};

// Traffic light indicator component
const TrafficLight = ({ color }) => {
  const colors = {
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    green: 'bg-green-500',
    gray: 'bg-gray-300'
  };
  return (
    <div className={`w-4 h-4 rounded-full ${colors[color] || colors.gray}`} />
  );
};

// Maintenance Recovery Email Settings Panel (for Settings page)
function MaintenanceEmailSettingsPanel() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { default: api } = await import('../services/api');
        const r = await api.get('/maintenance/email-settings');
        setSettings(r.data);
      } catch {
        setLoadError(true);
      }
    };
    loadSettings();
  }, []);

  const update = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    setSaving(true);
    try {
      const { default: api } = await import('../services/api');
      await api.put('/maintenance/email-settings', updated);
      toast({ title: 'Saved', description: 'Maintenance email settings updated' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loadError) return <p className="text-sm text-muted-foreground">Unable to load maintenance email settings.</p>;
  if (!settings) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        When maintenance mode is deactivated, the system can automatically email users who were force-logged out to let them know the system is back online.
      </p>
      <div className="space-y-3">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-recovery-email-toggle">
          <Switch
            checked={settings.send_recovery_email}
            onCheckedChange={(v) => update('send_recovery_email', v)}
          />
          <div>
            <div className="text-sm font-medium">Send recovery emails on deactivation</div>
            <div className="text-xs text-muted-foreground">Automatically email force-logged-out users when the system is back online</div>
          </div>
        </label>
        {settings.send_recovery_email && (
          <div className="ml-12 space-y-3 border-l-2 border-blue-200 pl-4">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-recovery-internal-toggle">
              <Switch
                checked={settings.recovery_email_internal}
                onCheckedChange={(v) => update('recovery_email_internal', v)}
              />
              <span className="text-sm">Internal users (staff, auditors, schedulers)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-recovery-external-toggle">
              <Switch
                checked={settings.recovery_email_external}
                onCheckedChange={(v) => update('recovery_email_external', v)}
              />
              <span className="text-sm">External users (clients)</span>
            </label>
          </div>
        )}
        {saving && <span className="text-xs text-muted-foreground ml-12">Saving...</span>}
      </div>
    </div>
  );
}


// Item 11: Searchable, case-insensitive alphabetical client picker for AP threshold overrides
function ClientApPicker({ clientGroups, value, onChange }) {
  const [search, setSearch] = useState('');
  const sorted = useMemo(() => {
    return [...(clientGroups || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [clientGroups]);
  const filtered = search.trim()
    ? sorted.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : sorted;
  const selectedName = (clientGroups || []).find(c => String(c.id) === value)?.name;
  return (
    <div className="mt-1 space-y-1">
      <input
        type="text"
        placeholder="Search clients…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full h-8 px-2 text-sm border border-input rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
        data-testid="ap-client-picker-search"
      />
      <Select value={value} onValueChange={v => { setSearch(''); onChange(v); }}>
        <SelectTrigger className="h-8" data-testid="ap-client-select">
          <SelectValue placeholder="— pick a client —">
            {selectedName || '— pick a client —'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No clients match</div>
          )}
          {filtered.map(c => (
            <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


// Multi-select dropdown for assigning groups/sites to custom thresholds
const MultiSelectDropdown = ({ clientGroups, allSites, selectedClientIds, selectedSiteIds, onChange, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  const toggleClient = (clientId) => {
    const newIds = selectedClientIds.includes(clientId)
      ? selectedClientIds.filter(id => id !== clientId)
      : [...selectedClientIds, clientId];
    onChange(newIds, selectedSiteIds);
  };
  
  const toggleSite = (siteId) => {
    const newIds = selectedSiteIds.includes(siteId)
      ? selectedSiteIds.filter(id => id !== siteId)
      : [...selectedSiteIds, siteId];
    onChange(selectedClientIds, newIds);
  };
  
  const removeClient = (clientId) => {
    onChange(selectedClientIds.filter(id => id !== clientId), selectedSiteIds);
  };
  
  const removeSite = (siteId) => {
    onChange(selectedClientIds, selectedSiteIds.filter(id => id !== siteId));
  };
  
  const filteredClients = clientGroups.filter(c => 
    !search || c.name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredSites = allSites.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase())
  );
  
  const totalSelected = selectedClientIds.length + selectedSiteIds.length;
  
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-left font-normal h-auto min-h-[36px] py-1.5"
            data-testid={`${testIdPrefix}-select-trigger`}
          >
            {totalSelected === 0 ? (
              <span className="text-muted-foreground text-xs">Select groups or sites...</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedClientIds.map(id => {
                  const client = clientGroups.find(c => c.id === id);
                  return client ? (
                    <Badge key={`c-${id}`} variant="secondary" className="text-[10px] py-0 px-1.5 gap-0.5">
                      <Building2 className="h-3 w-3" />
                      {client.name}
                      <button onClick={(e) => { e.stopPropagation(); removeClient(id); }} className="ml-0.5 hover:text-red-600">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ) : null;
                })}
                {selectedSiteIds.map(id => {
                  const site = allSites.find(s => s.id === id);
                  return site ? (
                    <Badge key={`s-${id}`} variant="outline" className="text-[10px] py-0 px-1.5 gap-0.5">
                      <MapPinned className="h-3 w-3" />
                      {site.name}
                      <button onClick={(e) => { e.stopPropagation(); removeSite(id); }} className="ml-0.5 hover:text-red-600">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-2 border-b">
            <Input
              placeholder="Search groups or sites..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredClients.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 py-1">Groups / Customers</p>
                {filteredClients.map(client => (
                  <label
                    key={`c-${client.id}`}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 rounded cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selectedClientIds.includes(client.id)}
                      onCheckedChange={() => toggleClient(client.id)}
                    />
                    <Building2 className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                    <span className="truncate">{client.name}</span>
                  </label>
                ))}
              </div>
            )}
            {filteredSites.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 py-1 mt-1 border-t">Individual Sites</p>
                {filteredSites.map(site => (
                  <label
                    key={`s-${site.id}`}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 rounded cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selectedSiteIds.includes(site.id)}
                      onCheckedChange={() => toggleSite(site.id)}
                    />
                    <MapPinned className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                    <span className="truncate">{site.name}</span>
                    {site.parent_client?.name && (
                      <span className="text-[10px] text-muted-foreground ml-auto flex-shrink-0">({site.parent_client.name})</span>
                    )}
                  </label>
                ))}
              </div>
            )}
            {filteredClients.length === 0 && filteredSites.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">No results found</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const SectionHeader = ({ title, subtitle }) => (
  <div className="pt-4 first:pt-0" data-testid={`section-header-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <h2 className="text-base font-semibold text-slate-900 tracking-tight">{title}</h2>
    {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
  </div>
);

export default function SettingsPage() {
  const { isAdmin, isScheduler } = useAuth();
  const { refreshSettings } = useSettings();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Organisation state
  const [organisation, setOrganisation] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [orgLogoUrl, setOrgLogoUrl] = useState('');
  const [orgBrandColor, setOrgBrandColor] = useState('#1a365d');
  const [savingOrg, setSavingOrg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Email Script Dialog state
  const [emailScriptDialogOpen, setEmailScriptDialogOpen] = useState(false);
  const [tempEmailScript, setTempEmailScript] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  
  // Traffic light thresholds state
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);

  // D2 — Org-level AP variance threshold defaults
  // NULL = inactive (unconfigured). These are org-wide defaults;
  // per-client ActionPlanConfig thresholds take precedence.
  const [apThresholdLow,  setApThresholdLow]  = useState('');
  const [apThresholdHigh, setApThresholdHigh] = useState('');

  // Feature 1 — AP Notifications toggle
  const [apNotificationsEnabled, setApNotificationsEnabled] = useState(true);
  const [togglingApNotif, setTogglingApNotif] = useState(false);
  const [apNotifStats, setApNotifStats] = useState(null);

  // D3 — Per-client AP threshold overrides (Settings page; dropdown picks one client)
  const [selectedClientForAP, setSelectedClientForAP] = useState('');
  const [clientApConfigs, setClientApConfigs] = useState([]); // [{scope, ap_enabled, threshold_low, threshold_high}]
  const [clientApConfigsLoading, setClientApConfigsLoading] = useState(false);
  
  // Clients (groups) and sites for custom threshold assignment
  const [clientGroups, setClientGroups] = useState([]);
  const [allSites, setAllSites] = useState([]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await settingsService.get();
      setSettings(data);
      
      // Load thresholds from settings or use defaults
      if (data.summary_report_thresholds) {
        setThresholds({ ...DEFAULT_THRESHOLDS, ...data.summary_report_thresholds });
      }
      // D2 — Load AP threshold defaults (nullable = unconfigured = inactive)
      setApThresholdLow( data.ap_threshold_low  != null ? String(data.ap_threshold_low)  : '');
      setApThresholdHigh(data.ap_threshold_high != null ? String(data.ap_threshold_high) : '');
      // Feature 1 — AP Notifications toggle
      setApNotificationsEnabled(data.ap_notifications_enabled !== false);

      // Feature 1 — AP Notification stats (non-critical, ignore failures)
      try {
        const stats = await actionPlansService.getNotificationStats();
        setApNotifStats(stats);
      } catch (_) { /* non-critical */ }
      
      // Load organisation details
      try {
        const orgData = await settingsService.getOrganisation();
        setOrganisation(orgData);
        setOrgName(orgData.name || '');
        setOrgLogoUrl(orgData.logo_url || '');
        setOrgBrandColor(orgData.brand_color || '#1a365d');
      } catch (err) {
        console.error('Failed to load organisation:', err);
      }
      
      // Load clients and sites for threshold assignment
      try {
        const [clientsData, sitesData] = await Promise.all([
          clientsService.list(),
          sitesService.list()
        ]);
        setClientGroups(clientsData || []);
        setAllSites(sitesData || []);
      } catch (err) {
        console.error('Failed to load clients/sites:', err);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // D3 — Load AP threshold configs for a specific client
  const loadClientApConfigs = useCallback(async (clientId) => {
    if (!clientId) { setClientApConfigs([]); return; }
    setClientApConfigsLoading(true);
    try {
      const data = await actionPlansService.getClientConfigs(clientId);
      setClientApConfigs(data || []);
    } catch (err) {
      console.error('Failed to load client AP configs:', err);
      setClientApConfigs([]);
    } finally {
      setClientApConfigsLoading(false);
    }
  }, []);

  // D3 — Upsert per-client AP threshold config for one scope
  const saveClientApConfig = async (clientId, scope, apEnabled, thresholdLow, thresholdHigh) => {
    try {
      await actionPlansService.upsertClientConfig(clientId, scope, {
        ap_enabled: apEnabled,
        threshold_low:  thresholdLow  !== '' && thresholdLow  != null ? parseFloat(thresholdLow)  : null,
        threshold_high: thresholdHigh !== '' && thresholdHigh != null ? parseFloat(thresholdHigh) : null,
      });
      await loadClientApConfigs(clientId);
      toast({ title: 'Saved', description: `${scope} threshold override saved.` });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save client threshold.', variant: 'destructive' });
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handleThresholdChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setThresholds(prev => ({ ...prev, [field]: numValue }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsService.update({
        default_region: settings.default_region,
        show_bank_holidays: settings.show_bank_holidays,
        allow_unassigned_jobs: settings.allow_unassigned_jobs,
        require_auditor_before_completion: settings.require_auditor_before_completion,
        enable_colour_coding: settings.enable_colour_coding,
        forecast_allocation_days: settings.forecast_allocation_days,
        forecast_allocation_auditors: settings.forecast_allocation_auditors,
        default_stock_period_days: settings.default_stock_period_days,
        summary_reports_mode: settings.summary_reports_mode,
        schedule_publication_email_script: settings.schedule_publication_email_script,
        reports_auditor_can_set_next_date: settings.reports_auditor_can_set_next_date,
        reports_publish_mode: settings.reports_publish_mode,
        reports_allow_draft_uploads: settings.reports_allow_draft_uploads,
        summary_reports_publish_mode: settings.summary_reports_publish_mode,
        summary_report_approval_emails: settings.summary_report_approval_emails,
        summary_report_thresholds: thresholds,
        // D2 — AP variance threshold defaults (null = inactive/unconfigured)
        ap_threshold_low:  apThresholdLow  !== '' ? parseFloat(apThresholdLow)  : null,
        ap_threshold_high: apThresholdHigh !== '' ? parseFloat(apThresholdHigh) : null,
        // Schedule change notification settings
        schedule_change_notifications_enabled: settings.schedule_change_notifications_enabled,
        schedule_notify_scheduler: settings.schedule_notify_scheduler,
        schedule_notify_senior_scheduler: settings.schedule_notify_senior_scheduler,
        schedule_notify_admin: settings.schedule_notify_admin
      });
      toast({ title: 'Success', description: 'Settings saved' });
      setHasChanges(false);
      // Refresh the global settings context
      refreshSettings();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save settings'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleApNotifications = async (newValue) => {
    setTogglingApNotif(true);
    try {
      const { apNotificationsService } = await import('../services/index.js');
      await apNotificationsService.toggle(newValue);
      setApNotificationsEnabled(newValue);
      toast({
        title: newValue ? 'AP Notifications Enabled' : 'AP Notifications Disabled',
        description: newValue
          ? 'AP notifications, reminders and retries are now active.'
          : 'All AP notifications are suppressed. Any notifications queued while OFF will be permanently skipped.',
      });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to update AP notifications'), variant: 'destructive' });
    } finally {
      setTogglingApNotif(false);
    }
  };

  const handleSaveOrganisation = async (overrideData = null) => {
    setSavingOrg(true);
    try {
      const dataToSave = overrideData || {
        name: orgName,
        logo_url: orgLogoUrl || null,
        brand_color: orgBrandColor || '#1a365d'
      };
      const result = await settingsService.updateOrganisation(dataToSave);
      setOrganisation(result);
      setOrgName(result.name || '');
      setOrgLogoUrl(result.logo_url || '');
      setOrgBrandColor(result.brand_color || '#1a365d');
      toast({ title: 'Success', description: 'Organisation details saved' });
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save organisation'),
        variant: 'destructive'
      });
    } finally {
      setSavingOrg(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 500KB for email embedding)
    if (file.size > 500 * 1024) {
      toast({
        title: 'Error',
        description: 'Logo file must be smaller than 500KB for email compatibility',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Error',
        description: 'Please upload a PNG, JPEG, GIF, or WebP image',
        variant: 'destructive'
      });
      return;
    }

    setUploadingLogo(true);
    try {
      const result = await settingsService.uploadOrganisationLogo(file);
      setOrgLogoUrl(result.logo_url);
      setOrganisation(prev => ({ ...prev, logo_url: result.logo_url }));
      toast({ title: 'Success', description: 'Logo uploaded successfully' });
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to upload logo'),
        variant: 'destructive'
      });
    } finally {
      setUploadingLogo(false);
      // Reset file input
      e.target.value = '';
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Settings
          </h1>
          <p className="text-muted-foreground">Configure organisation preferences</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving || !hasChanges}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={[]} className="space-y-4">
        {/* === Setup === */}
        <SectionHeader title="Setup" subtitle="Foundational configuration for your organisation" />
        {/* Organisation Settings */}
        <AccordionItem value="organisation" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              <span className="font-semibold">Organisation Details</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure your organisation name and branding</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organisation Name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Enter organisation name"
                  data-testid="org-name-input"
                />
                <p className="text-sm text-muted-foreground">
                  This name appears in client email notifications
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Company Logo</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="max-w-xs"
                    data-testid="org-logo-upload"
                  />
                  {uploadingLogo && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
                <p className="text-sm text-muted-foreground">
                  Upload a PNG, JPEG, or GIF image (max 500KB). Displayed in email headers.
                </p>
              </div>
              
              {orgLogoUrl && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <Label className="text-sm mb-2 block">Current Logo</Label>
                  <div className="flex items-center gap-4">
                    <img 
                      src={orgLogoUrl} 
                      alt="Organisation logo" 
                      className="max-h-20 max-w-[250px] object-contain border rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setOrgLogoUrl('');
                        handleSaveOrganisation({ logo_url: null });
                      }}
                      data-testid="remove-logo-btn"
                    >
                      Remove Logo
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="brand-color">Brand Color</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="brand-color"
                    type="color"
                    value={orgBrandColor}
                    onChange={(e) => setOrgBrandColor(e.target.value)}
                    className="w-16 h-10 p-1 cursor-pointer"
                    data-testid="brand-color-picker"
                  />
                  <Input
                    type="text"
                    value={orgBrandColor}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                        setOrgBrandColor(val);
                      }
                    }}
                    placeholder="#1a365d"
                    className="w-28"
                    data-testid="brand-color-input"
                  />
                  <div 
                    className="w-20 h-10 rounded border flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: orgBrandColor }}
                  >
                    Preview
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  This color is used for email headers and table styling in client notifications
                </p>
              </div>
              
              <Button 
                onClick={() => handleSaveOrganisation()}
                disabled={savingOrg || (
                  orgName === organisation?.name && 
                  orgLogoUrl === (organisation?.logo_url || '') &&
                  orgBrandColor === (organisation?.brand_color || '#1a365d')
                )}
                variant="secondary"
                data-testid="save-org-btn"
              >
                {savingOrg ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Organisation Details
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Region Settings */}
        <AccordionItem value="region" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              <span className="font-semibold">Region & Currency</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure your region for bank holidays and currency</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Environment Region</Label>
                  <p className="text-sm text-muted-foreground">
                    Determines currency (£/AED) and bank holidays
                  </p>
                </div>
                <Select 
                  value={settings?.default_region || 'UK'} 
                  onValueChange={(v) => handleChange('default_region', v)}
                >
                  <SelectTrigger className="w-[200px]" data-testid="region-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UK">
                      <div className="flex items-center gap-2">
                        <span>🇬🇧</span>
                        <span>United Kingdom (£)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="UAE">
                      <div className="flex items-center gap-2">
                        <span>🇦🇪</span>
                        <span>UAE (AED)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Current Region Display */}
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Current Environment</span>
                  <span className="text-lg font-bold flex items-center gap-2">
                    {settings?.default_region === 'UAE' ? '🇦🇪 UAE' : '🇬🇧 UK'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Currency: {settings?.default_region === 'UAE' ? 'AED' : '£'} • 
                  Bank Holidays: {settings?.default_region === 'UAE' ? 'UAE public holidays' : 'UK bank holidays'}
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Calendar & Job Completion Settings */}
        <AccordionItem value="calendar" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <span className="font-semibold">Calendar & Scheduling</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure calendar display and scheduling rules</p>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Bank Holidays</Label>
                  <p className="text-sm text-muted-foreground">
                    Display bank holidays on the calendar view
                  </p>
                </div>
                <Switch
                  checked={settings?.show_bank_holidays ?? true}
                  onCheckedChange={(checked) => handleChange('show_bank_holidays', checked)}
                  data-testid="show-holidays-toggle"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow Unassigned Jobs</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow creating schedule entries without assigning auditors
                  </p>
                </div>
                <Switch
                  checked={settings?.allow_unassigned_jobs ?? true}
                  onCheckedChange={(checked) => handleChange('allow_unassigned_jobs', checked)}
                  data-testid="allow-unassigned-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Require Auditor Before Completion</Label>
                  <p className="text-sm text-muted-foreground">
                    Jobs must have at least one auditor assigned before marking complete
                  </p>
                </div>
                <Switch
                  checked={settings?.require_auditor_before_completion ?? true}
                  onCheckedChange={(checked) => handleChange('require_auditor_before_completion', checked)}
                  data-testid="require-auditor-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Colour Coding</Label>
                  <p className="text-sm text-muted-foreground">
                    Show customer colours in schedule and calendar views
                  </p>
                </div>
                <Switch
                  checked={settings?.enable_colour_coding ?? true}
                  onCheckedChange={(checked) => handleChange('enable_colour_coding', checked)}
                  data-testid="colour-coding-toggle"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Schedule Change Notifications */}
        
        {/* Region Management */}
        <RegionSettings />
        
        {/* === Communication === */}
        <SectionHeader title="Communication" subtitle="How auditors, clients and admins are notified" />
        <AccordionItem value="email-diagnostics" className="border rounded-lg" data-testid="email-diagnostics-accordion-item">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              <span className="font-semibold">Email Diagnostics</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-3">
              Inspect deliverability of invitation, temp-password and schedule emails. See exactly which messages SendGrid accepted, which failed, and whether the sender domain is properly authenticated (SPF/DKIM). Also surfaces which URL email links currently point at — so you can spot staging-vs-production drift after a deploy.
            </p>
            <Button variant="default" onClick={() => navigate('/settings/email-diagnostics')} data-testid="open-email-diagnostics-btn">
              <Mail className="h-4 w-4 mr-2" /> Open Email Diagnostics
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="notifications" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              <span className="font-semibold">Schedule Change Notifications</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure email notifications when schedule changes are made</p>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Send email notifications when jobs are added, modified, or deleted from the schedule or batch matrix
                  </p>
                </div>
                <Switch
                  checked={settings?.schedule_change_notifications_enabled ?? false}
                  onCheckedChange={(checked) => handleChange('schedule_change_notifications_enabled', checked)}
                  data-testid="schedule-notifications-toggle"
                />
              </div>
              
              {settings?.schedule_change_notifications_enabled && (
                <div className="space-y-4 border-t pt-4">
                  <Label className="text-sm font-medium">Notify the following roles:</Label>
                  <div className="space-y-3 ml-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Scheduler</Label>
                        <p className="text-xs text-muted-foreground">Notify assigned schedulers</p>
                      </div>
                      <Switch
                        checked={settings?.schedule_notify_scheduler ?? false}
                        onCheckedChange={(checked) => handleChange('schedule_notify_scheduler', checked)}
                        data-testid="notify-scheduler-toggle"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Senior Scheduler</Label>
                        <p className="text-xs text-muted-foreground">Notify senior schedulers</p>
                      </div>
                      <Switch
                        checked={settings?.schedule_notify_senior_scheduler ?? false}
                        onCheckedChange={(checked) => handleChange('schedule_notify_senior_scheduler', checked)}
                        data-testid="notify-senior-scheduler-toggle"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Administrator</Label>
                        <p className="text-xs text-muted-foreground">Notify system administrators</p>
                      </div>
                      <Switch
                        checked={settings?.schedule_notify_admin ?? false}
                        onCheckedChange={(checked) => handleChange('schedule_notify_admin', checked)}
                        data-testid="notify-admin-toggle"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Forecast Allocation Settings */}
        {/* === Stock Audit === */}
        <SectionHeader title="Stock Audit" subtitle="Forecasts, methodology, publication, and approval workflows" />
        <AccordionItem value="forecast" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <PoundSterling className="h-5 w-5" />
              <span className="font-semibold">Forecast Allocation</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure how revenue is allocated in the Forecast report</p>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Multi-Day Jobs</Label>
                  <p className="text-sm text-muted-foreground">
                    How to allocate revenue for jobs spanning multiple days
                  </p>
                </div>
                <Select 
                  value={settings?.forecast_allocation_days || 'first_day'} 
                  onValueChange={(v) => handleChange('forecast_allocation_days', v)}
                >
                  <SelectTrigger className="w-[220px]" data-testid="allocation-days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_day">Allocate to first day only</SelectItem>
                    <SelectItem value="equal_days">Allocate equally across all days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Multi-Auditor Jobs</Label>
                  <p className="text-sm text-muted-foreground">
                    How to allocate revenue for jobs with multiple auditors
                  </p>
                </div>
                <Select 
                  value={settings?.forecast_allocation_auditors || 'equal_auditors'} 
                  onValueChange={(v) => handleChange('forecast_allocation_auditors', v)}
                >
                  <SelectTrigger className="w-[220px]" data-testid="allocation-auditors-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_auditor">Allocate to first auditor only</SelectItem>
                    <SelectItem value="equal_auditors">Allocate equally across all auditors</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Stock Audit Report Settings - REFACTORED with Accordion */}
        <AccordionItem value="stockaudit" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <span className="font-semibold">Stock Audit Report</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure defaults, workflow, and traffic light thresholds for Summary Reports</p>
            <Accordion type="multiple" defaultValue={["general", "thresholds"]} className="w-full">
              {/* General Settings */}
              <AccordionItem value="general">
                <AccordionTrigger className="text-sm font-medium">
                  General Settings
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-6 pt-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="stock-period-days">Default Stock Period (Days)</Label>
                        <p className="text-sm text-muted-foreground">
                          Default number of days between the previous audit and the current
                          audit (e.g. 28 for a 4-weekly cadence). The period starts on the day
                          of the last audit and ends at midnight before the audit date — the
                          audit day itself is not counted.
                        </p>
                      </div>
                      <Input
                        id="stock-period-days"
                        type="number"
                        min="1"
                        max="365"
                        value={settings?.default_stock_period_days ?? 28}
                        onChange={(e) => handleChange('default_stock_period_days', parseInt(e.target.value) || 28)}
                        className="w-[120px]"
                        data-testid="stock-period-days-input"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="summary-reports-mode">Summary Reports Mode</Label>
                        <p className="text-sm text-muted-foreground">
                          Control whether Summary Reports are optional or required for eligible Stock Audit jobs
                        </p>
                      </div>
                      <Select
                        value={settings?.summary_reports_mode || 'OPTIONAL'}
                        onValueChange={(value) => handleChange('summary_reports_mode', value)}
                      >
                        <SelectTrigger className="w-[220px]" data-testid="summary-reports-mode-select">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OPTIONAL">Optional</SelectItem>
                          <SelectItem value="MANDATORY_FOR_ELIGIBLE_JOBS">Mandatory for Eligible Jobs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="auditor-can-set-next-date">Allow Auditor to Set Next Audit Date</Label>
                        <p className="text-sm text-muted-foreground">
                          When enabled, auditors can manually input the next audit date
                        </p>
                      </div>
                      <Switch
                        id="auditor-can-set-next-date"
                        checked={settings?.reports_auditor_can_set_next_date ?? false}
                        onCheckedChange={(checked) => handleChange('reports_auditor_can_set_next_date', checked)}
                        data-testid="auditor-can-set-next-date-switch"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              {/* Traffic Light Thresholds */}
              <AccordionItem value="thresholds">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Traffic Light Thresholds
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-6 pt-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      Configure the thresholds for traffic light indicators in the Results Breakdown section of Summary Reports.
                    </div>
                    
                    {/* Stock Result % Thresholds - Universal Criteria */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        Stock Result % - Universal Criteria
                        <span className="text-xs text-muted-foreground font-normal">(Applies to all customers/sites unless overridden)</span>
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <TrafficLight color="green" />
                          <div className="flex-1">
                            <Label className="text-xs text-green-700">GREEN: Between</Label>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={thresholds.stock_result_green_upper ?? 0.5}
                                  onChange={(e) => handleThresholdChange('stock_result_green_upper', e.target.value)}
                                  className="w-20 h-8 text-sm"
                                  data-testid="threshold-stock-green-upper"
                                />
                                <span className="text-sm">%</span>
                              </div>
                              <span className="text-xs">to</span>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={thresholds.stock_result_green_lower ?? -2.0}
                                  onChange={(e) => handleThresholdChange('stock_result_green_lower', e.target.value)}
                                  className="w-20 h-8 text-sm"
                                  data-testid="threshold-stock-green-lower"
                                />
                                <span className="text-sm">%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                          <TrafficLight color="red" />
                          <div className="flex-1">
                            <Label className="text-xs text-red-700">RED: Outside Green Range</Label>
                            <p className="text-xs text-red-600">
                              Over {thresholds.stock_result_green_upper ?? 0.5}% or below {thresholds.stock_result_green_lower ?? -2.0}%
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        Example: With defaults (+0.5% to -2.0%), a result of -1.5% = GREEN, -2.5% = RED, +0.8% = RED
                      </p>
                    </div>
                    
                    {/* Customer/Site-Specific Thresholds */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          Customer/Site-Specific Criteria
                          <span className="text-xs text-muted-foreground font-normal">(Overrides universal criteria)</span>
                        </h4>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newThreshold = {
                              id: `custom-${Date.now()}`,
                              name: '',
                              client_ids: [],
                              site_ids: [],
                              threshold: {
                                green_upper: 0.5,
                                green_lower: -2.0,
                                red_upper: null,
                                red_lower: -5.0
                              }
                            };
                            setThresholds(prev => ({
                              ...prev,
                              custom_thresholds: [...(prev.custom_thresholds || []), newThreshold]
                            }));
                            setHasChanges(true);
                          }}
                          data-testid="add-custom-threshold-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Custom Criteria
                        </Button>
                      </div>
                      
                      {(!thresholds.custom_thresholds || thresholds.custom_thresholds.length === 0) ? (
                        <p className="text-sm text-muted-foreground italic py-4 text-center bg-slate-50 rounded">
                          No custom criteria defined. Universal criteria applies to all customers/sites.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {thresholds.custom_thresholds.map((custom, idx) => (
                            <div key={custom.id || idx} className="border rounded-lg p-4 bg-slate-50 space-y-3">
                              <div className="flex items-center justify-between">
                                <Input
                                  placeholder="Rule name (e.g., 'Extended tolerance for Hilton')"
                                  value={custom.name || ''}
                                  onChange={(e) => {
                                    const updated = [...thresholds.custom_thresholds];
                                    updated[idx] = { ...updated[idx], name: e.target.value };
                                    setThresholds(prev => ({ ...prev, custom_thresholds: updated }));
                                    setHasChanges(true);
                                  }}
                                  className="max-w-md"
                                  data-testid={`custom-threshold-${idx}-name`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = thresholds.custom_thresholds.filter((_, i) => i !== idx);
                                    setThresholds(prev => ({ ...prev, custom_thresholds: updated }));
                                    setHasChanges(true);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`custom-threshold-${idx}-delete`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {/* Group/Site Multi-Select */}
                              <div className="space-y-2">
                                <Label className="text-xs font-medium">Apply to Groups / Sites:</Label>
                                <MultiSelectDropdown
                                  clientGroups={clientGroups}
                                  allSites={allSites}
                                  selectedClientIds={custom.client_ids || []}
                                  selectedSiteIds={custom.site_ids || []}
                                  onChange={(clientIds, siteIds) => {
                                    const updated = [...thresholds.custom_thresholds];
                                    updated[idx] = { ...updated[idx], client_ids: clientIds, site_ids: siteIds };
                                    setThresholds(prev => ({ ...prev, custom_thresholds: updated }));
                                    setHasChanges(true);
                                  }}
                                  testIdPrefix={`custom-threshold-${idx}`}
                                />
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <Label className="text-xs text-green-700">GREEN: Between</Label>
                                  <div className="flex items-center gap-1 mt-1">
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={custom.threshold?.green_upper ?? 0.5}
                                      onChange={(e) => {
                                        const updated = [...thresholds.custom_thresholds];
                                        updated[idx] = { 
                                          ...updated[idx], 
                                          threshold: { ...updated[idx].threshold, green_upper: parseFloat(e.target.value) || 0 }
                                        };
                                        setThresholds(prev => ({ ...prev, custom_thresholds: updated }));
                                        setHasChanges(true);
                                      }}
                                      className="w-16 h-7 text-sm"
                                    />
                                    <span className="text-xs">% to</span>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={custom.threshold?.green_lower ?? -2.0}
                                      onChange={(e) => {
                                        const updated = [...thresholds.custom_thresholds];
                                        updated[idx] = { 
                                          ...updated[idx], 
                                          threshold: { ...updated[idx].threshold, green_lower: parseFloat(e.target.value) || 0 }
                                        };
                                        setThresholds(prev => ({ ...prev, custom_thresholds: updated }));
                                        setHasChanges(true);
                                      }}
                                      className="w-16 h-7 text-sm"
                                    />
                                    <span className="text-xs">%</span>
                                  </div>
                                </div>
                                
                                <div className="p-2 bg-amber-50 rounded border border-amber-200">
                                  <Label className="text-xs text-amber-700">AMBER: Between</Label>
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className="text-xs">{custom.threshold?.green_lower ?? -2.0}% to</span>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={custom.threshold?.red_lower ?? -5.0}
                                      onChange={(e) => {
                                        const updated = [...thresholds.custom_thresholds];
                                        updated[idx] = { 
                                          ...updated[idx], 
                                          threshold: { ...updated[idx].threshold, red_lower: parseFloat(e.target.value) || 0 }
                                        };
                                        setThresholds(prev => ({ ...prev, custom_thresholds: updated }));
                                        setHasChanges(true);
                                      }}
                                      className="w-16 h-7 text-sm"
                                    />
                                    <span className="text-xs">%</span>
                                  </div>
                                </div>
                                
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <Label className="text-xs text-red-700">RED: Outside</Label>
                                  <p className="text-xs text-red-600 mt-1">
                                    Over {custom.threshold?.green_upper ?? 0.5}% or below {custom.threshold?.red_lower ?? -5.0}%
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* COS vs Budget Thresholds */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        Cost of Sales % vs Budget %
                        <span className="text-xs text-muted-foreground font-normal">(Actual vs Target)</span>
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <TrafficLight color="green" />
                          <div className="flex-1">
                            <Label className="text-xs text-green-700">GREEN: At or below budget</Label>
                            <p className="text-xs text-green-600">Actual COS % &le; Budget %</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <TrafficLight color="amber" />
                          <div className="flex-1">
                            <Label className="text-xs text-amber-700">AMBER: Over budget by up to</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                value={thresholds.cos_variance_amber}
                                onChange={(e) => handleThresholdChange('cos_variance_amber', e.target.value)}
                                className="w-20 h-8 text-sm"
                                data-testid="threshold-cos-amber"
                              />
                              <span className="text-sm">%</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                          <TrafficLight color="red" />
                          <div className="flex-1">
                            <Label className="text-xs text-red-700">RED: Over budget by more than</Label>
                            <p className="text-xs text-red-600">
                              {thresholds.cos_variance_amber}%
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        Example (amber tolerance 0.5%): Budget 25%, Actual 24.7% (variance &minus;0.3%): <span className="font-medium text-green-700">GREEN</span> because actual is under budget.
                        <br />
                        Actual 25.3% (variance +0.3%): <span className="font-medium text-amber-700">AMBER</span> because it is over budget but within 0.5%.
                        Actual 26.0% (variance +1.0%): <span className="font-medium text-red-700">RED</span> because it exceeds the amber tolerance.
                      </p>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setThresholds(DEFAULT_THRESHOLDS);
                        setHasChanges(true);
                      }}
                      data-testid="reset-thresholds-btn"
                    >
                      Reset to Defaults
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Client Report Metrics Templates */}
              <AccordionItem value="metrics-templates">
                <AccordionTrigger className="text-sm font-medium">
                  Client Report Metrics Templates
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-2">
                    <MetricsTemplatesSettings />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContent>
        </AccordionItem>

        {/* ── Action Plan Section ───────────────────────────────────────────── */}
        <SectionHeader
          title="Action Plan"
          subtitle="Variance thresholds and per-client overrides — Beverage scope only"
        />
        <AccordionItem value="actionplan-main" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <ListChecks className="h-5 w-5" />
              <div>
                <p className="font-medium text-left">Action Plan Settings</p>
                <p className="text-xs text-muted-foreground font-normal text-left">
                  Variance thresholds that control when an Action Plan is triggered
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <Accordion type="multiple" defaultValue={['ap-thresholds']} className="space-y-2">

              {/* D2 — Org-level AP Variance Thresholds */}
              <AccordionItem value="ap-thresholds">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    Organisation AP Variance Thresholds
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      Org-level AP threshold defaults (Beverage). Precedence: per-client overrides (below) &gt; these org defaults &gt; inactive (unconfigured).
                      Leave blank to keep unconfigured — no AP trigger will use these defaults.
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Lower Threshold (%)</Label>
                        <p className="text-xs text-muted-foreground mb-1">
                          e.g. &minus;5 for &minus;5% variance
                        </p>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="Leave blank = inactive"
                          value={apThresholdLow}
                          onChange={(e) => { setApThresholdLow(e.target.value); setHasChanges(true); }}
                          className="h-8"
                          data-testid="ap-threshold-low-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Upper Threshold (%)</Label>
                        <p className="text-xs text-muted-foreground mb-1">
                          e.g. 5 for +5% variance
                        </p>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="Leave blank = inactive"
                          value={apThresholdHigh}
                          onChange={(e) => { setApThresholdHigh(e.target.value); setHasChanges(true); }}
                          className="h-8"
                          data-testid="ap-threshold-high-input"
                        />
                      </div>
                    </div>
                    {(apThresholdLow !== '' || apThresholdHigh !== '') && (
                      <p className="text-xs text-muted-foreground">
                        Configured: AP may trigger when variance &lt; {apThresholdLow || '?'}% or &gt; {apThresholdHigh || '?'}%.
                        Per-client thresholds override these org defaults.
                      </p>
                    )}

                    {/* Feature 1 — AP Notifications Toggle */}
                    <div className="mt-6 pt-4 border-t">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium leading-none">AP Notifications</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            When <strong>OFF</strong>, all AP notification sends, reminders and retries are suppressed.
                            Any notifications queued while disabled are <strong>permanently skipped</strong> — they
                            will not be sent after re-enabling.
                          </p>
                          {!apNotificationsEnabled && (
                            <p className="mt-2 text-xs font-semibold text-red-600 flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                              AP notifications are currently suppressed
                            </p>
                          )}
                          {apNotifStats && (
                            <div
                              className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs space-y-2"
                              data-testid="ap-notif-stats-panel"
                            >
                              <p className="font-semibold text-slate-700">Notification Log — Org Summary</p>
                              <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                                <span className="text-slate-500">Disabled: <span className="font-mono text-slate-700" data-testid="apns-disabled">{apNotifStats.counts.disabled}</span></span>
                                <span className="text-blue-700">Queued: <span className="font-mono font-semibold" data-testid="apns-queued">{apNotifStats.counts.queued}</span></span>
                                <span className="text-amber-700">Suppressed: <span className="font-mono font-semibold" data-testid="apns-suppressed">{apNotifStats.counts.suppressed_org}</span></span>
                                <span className="text-green-700">Sent: <span className="font-mono font-semibold" data-testid="apns-sent">{apNotifStats.counts.sent}</span></span>
                                <span className="text-red-700">Failed: <span className="font-mono" data-testid="apns-failed">{apNotifStats.counts.failed}</span></span>
                                <span className="text-slate-500">Captured: <span className="font-mono" data-testid="apns-captured">{apNotifStats.counts.captured}</span></span>
                              </div>
                              <p className="text-amber-700 font-medium border-t border-slate-200 pt-2"
                                 data-testid="apns-isolation-note">
                                {apNotifStats.isolation_note}
                              </p>
                              {!apNotifStats.ap_notifications_env_enabled && (
                                <p className="text-slate-500 italic" data-testid="apns-env-note">
                                  Env: AP_NOTIFICATIONS_ENABLED=0 — all new rows staged as <strong>disabled</strong> (no SendGrid delivery)
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          role="switch"
                          aria-checked={apNotificationsEnabled}
                          onClick={() => handleToggleApNotifications(!apNotificationsEnabled)}
                          disabled={togglingApNotif}
                          data-testid="ap-notifications-toggle"
                          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                            apNotificationsEnabled ? 'bg-green-600 focus:ring-green-500' : 'bg-gray-300 focus:ring-gray-400'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              apNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* D3 — Per-client AP Threshold Overrides (Beverage only) */}
              {(isAdmin || isScheduler) && (
                <AccordionItem value="ap-client-thresholds">
                  <AccordionTrigger className="text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <ListChecks className="h-4 w-4" />
                      Client AP Threshold Overrides
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      <p className="text-xs text-muted-foreground">
                        Override the org-level default for a specific client (Beverage scope).
                        Leave blank to inherit the org default. Per-client overrides take highest precedence.
                      </p>
                      <div>
                        <Label className="text-sm">Select Client</Label>
                        {/* Item 11: searchable, case-insensitive alphabetical picker */}
                        <ClientApPicker
                          clientGroups={clientGroups}
                          value={selectedClientForAP}
                          onChange={(val) => {
                            setSelectedClientForAP(val);
                            loadClientApConfigs(val);
                          }}
                        />
                      </div>

                      {selectedClientForAP && (
                        clientApConfigsLoading ? (
                          <p className="text-xs text-muted-foreground">Loading…</p>
                        ) : (
                          <div className="space-y-3" data-testid="ap-client-threshold-panel">
                            {['BEVERAGE'].map((scope) => {
                              const cfg = clientApConfigs.find((c) => c.scope === scope) || {};
                              return (
                                <div key={scope} className="border rounded p-3 bg-white space-y-2">
                                  <p className="text-xs font-semibold text-blue-700">{scope}</p>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs w-32 shrink-0">Lower threshold (%):</Label>
                                      <Input
                                        id={`settings-ap-thr-low-${scope}`}
                                        type="number"
                                        step="0.1"
                                        placeholder="org default"
                                        defaultValue={cfg.threshold_low != null ? String(cfg.threshold_low) : ''}
                                        className="h-7 w-24 text-xs"
                                        data-testid={`settings-ap-threshold-low-${scope.toLowerCase()}`}
                                      />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs w-32 shrink-0">Upper threshold (%):</Label>
                                      <Input
                                        id={`settings-ap-thr-high-${scope}`}
                                        type="number"
                                        step="0.1"
                                        placeholder="org default"
                                        defaultValue={cfg.threshold_high != null ? String(cfg.threshold_high) : ''}
                                        className="h-7 w-24 text-xs"
                                        data-testid={`settings-ap-threshold-high-${scope.toLowerCase()}`}
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      data-testid={`settings-ap-threshold-save-${scope.toLowerCase()}`}
                                      onClick={() => {
                                        const low  = document.getElementById(`settings-ap-thr-low-${scope}`)?.value;
                                        const high = document.getElementById(`settings-ap-thr-high-${scope}`)?.value;
                                        saveClientApConfig(selectedClientForAP, scope, cfg.ap_enabled ?? true, low, high);
                                      }}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

            </Accordion>
          </AccordionContent>
        </AccordionItem>
        
        {/* Client Publication Settings */}
        <AccordionItem value="publication" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              <span className="font-semibold">Client Publication</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure the default email script for schedule publication notifications</p>
            <div className="space-y-4">
              <div>
                <Label>Default Email Script</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  This email template will be used when sending schedule notifications to clients. 
                  Individual sites can override this with a custom script.
                </p>
                <div className="flex items-center gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setTempEmailScript(settings?.schedule_publication_email_script || '');
                      setEmailScriptDialogOpen(true);
                    }}
                    data-testid="edit-email-script-btn"
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit Email Script
                  </Button>
                  {settings?.schedule_publication_email_script ? (
                    <span className="text-sm text-muted-foreground">Script configured ({settings.schedule_publication_email_script.length} characters)</span>
                  ) : (
                    <span className="text-sm text-amber-600">No script configured - using default template</span>
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        {/* Reports Workflow Settings */}
        <AccordionItem value="workflow" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <span className="font-semibold">Reports Workflow</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">Configure report publishing, approval, and upload settings</p>
            <div className="space-y-6">
              {/* Job Report Publish Mode (legacy) */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="publish-mode">Job Report Publish Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Controls how uploaded audit report files (PDFs) are published
                  </p>
                </div>
                <Select
                  value={settings?.reports_publish_mode || 'direct'}
                  onValueChange={(value) => handleChange('reports_publish_mode', value)}
                >
                  <SelectTrigger className="w-[220px]" data-testid="publish-mode-select">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct Publish</SelectItem>
                    <SelectItem value="approval_required">Approval Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Summary Report Publish Mode (new) */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="summary-publish-mode">Summary Audit Report Approval</Label>
                  <p className="text-sm text-muted-foreground">
                    Controls how Summary Stock Audit Reports (wizard) are published
                  </p>
                </div>
                <Select
                  value={settings?.summary_reports_publish_mode || 'direct'}
                  onValueChange={(value) => handleChange('summary_reports_publish_mode', value)}
                >
                  <SelectTrigger className="w-[220px]" data-testid="summary-publish-mode-select">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct Publish</SelectItem>
                    <SelectItem value="approval_required">Approval Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Approval Email Notifications */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>Summary Report Approval Emails</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, schedulers are emailed when a report is submitted for approval, 
                    and auditors are emailed when their report is approved.
                  </p>
                </div>
                <Switch
                  checked={settings?.summary_report_approval_emails ?? false}
                  onCheckedChange={(checked) => handleChange('summary_report_approval_emails', checked)}
                  data-testid="summary-approval-emails-switch"
                />
              </div>
              
              {/* Allow Draft Uploads */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allow-draft-uploads">Allow Draft Uploads</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, uploaders can save reports as drafts. 
                    When disabled, uploads create unpublished status.
                  </p>
                </div>
                <Switch
                  id="allow-draft-uploads"
                  checked={settings?.reports_allow_draft_uploads ?? false}
                  onCheckedChange={(checked) => handleChange('reports_allow_draft_uploads', checked)}
                  data-testid="allow-draft-uploads-switch"
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 space-y-1">
                <p><strong>Job Report Publish Mode:</strong> Controls uploaded PDF files in Audit Reports. Direct = anyone publishes. Approval Required = auditors request, schedulers approve.</p>
                <p><strong>Summary Audit Report Approval:</strong> Controls the Summary Stock Audit wizard reports. Direct = auditor can publish directly. Approval Required = auditor submits for approval, PDF shows "Draft — Pending Approval" banner until scheduler approves.</p>
                <p><strong>Approval Emails:</strong> Sends email to all schedulers when an auditor submits for approval, and email to the auditor when their report is approved.</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Maintenance Recovery Emails */}
        {/* === Client Portal === */}
        <SectionHeader title="Client Portal" subtitle="What client users see when they log in" />
        <AccordionItem value="client-portal" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <span className="font-semibold">Client Portal</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-4">
              Override which dashboard features render for specific client portal users.
            </p>
            <Button
              variant="outline"
              onClick={() => window.location.assign('/settings/client-portal/dashboard-layouts')}
              data-testid="open-dashboard-layouts-btn"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Dashboard Layouts
            </Button>
          </AccordionContent>
        </AccordionItem>

        {/* === System === */}
        <SectionHeader title="System" subtitle="Maintenance and admin operations" />
        <AccordionItem value="maintenance" className="border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-slate-500" />
              <div className="text-left">
                <div className="font-semibold">Maintenance Recovery Emails</div>
                <div className="text-sm text-muted-foreground font-normal">Configure automatic notifications when maintenance ends</div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <MaintenanceEmailSettingsPanel />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      
      {/* Email Script Edit Dialog */}
      <Dialog open={emailScriptDialogOpen} onOpenChange={(open) => {
        setEmailScriptDialogOpen(open);
        if (!open) setShowEmailPreview(false);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Email Script</DialogTitle>
            <DialogDescription>
              Configure the default email template for schedule publication notifications. Format text using the toolbar above the editor.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {!showEmailPreview ? (
              <>
                <RichTextEditor
                  value={tempEmailScript}
                  onChange={setTempEmailScript}
                  placeholder="Dear {{contact_name}},

Please find below the scheduled audit details for {{site_name}}.

{{jobs_table}}

Best regards,
{{organisation_name}}"
                  minHeight="200px"
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>Available placeholders</strong> (type these exactly as shown):
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{contact_name}}'}</code>
                      <span className="text-xs">Recipient's name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{site_name}}'}</code>
                      <span className="text-xs">Site name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{customer_name}}'}</code>
                      <span className="text-xs">Customer name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{scheduled_date}}'}</code>
                      <span className="text-xs">First audit date</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{organisation_name}}'}</code>
                      <span className="text-xs">Your organisation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{jobs_table}}'}</code>
                      <span className="text-xs">Schedule table</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <EmailPreview
                emailScript={tempEmailScript}
                organisationName={orgName || 'Your Organisation'}
                logoUrl={orgLogoUrl}
                brandColor={orgBrandColor}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEmailPreview(!showEmailPreview)}
              data-testid="toggle-preview-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              {showEmailPreview ? 'Edit Script' : 'Preview Email'}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => {
              setEmailScriptDialogOpen(false);
              setShowEmailPreview(false);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                handleChange('schedule_publication_email_script', tempEmailScript);
                setEmailScriptDialogOpen(false);
                setShowEmailPreview(false);
                toast({ title: 'Script updated', description: 'Remember to save your settings' });
              }}
              data-testid="save-email-script-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
